import { useCallback, useEffect, useState } from 'react';
import { api, type AdminAccount } from '../api';
import { messageFor } from '../App';

/**
 * Operator console.
 *
 * Shows what running the service requires — who exists, what it costs, whether it is
 * healthy — and deliberately not the contents of anyone's log. Administering this app does
 * not require reading other families' children's records, and a screen that showed them
 * would make that the default rather than a decision. A family who needs support that
 * involves their entries can add the operator as a member, which is visible to them.
 */
export function AdminView() {
  const [rows, setRows] = useState<AdminAccount[]>([]);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      setRows((await api.adminAccounts()).accounts);
      setError(undefined);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function guard(action: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
      await load();
    } catch (err) {
      setError(messageFor(err));
      setBusy(false);
    }
  }

  return (
    <>
      {error && <div className="notice error">{error}</div>}

      <div className="panel">
        <h2>All accounts</h2>
        <p className="sub">
          {rows.length} account{rows.length === 1 ? '' : 's'}. Log entries are not shown here —
          see the note in the code for why.
        </p>

        {busy && rows.length === 0 && <p className="muted">Loading…</p>}

        {rows.map((row) => (
          <div key={row.accountId} className="panel" style={{ marginBottom: 12 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <div style={{ flex: 2 }}>
                <strong>{row.name}</strong>{' '}
                {row.status === 'suspended' && <span className="pill">suspended</span>}
                <div className="muted mono">{row.accountId}</div>
              </div>
              <div>
                <div className="muted">
                  {row.childCount} student{row.childCount === 1 ? '' : 's'} ·{' '}
                  {row.defaultJurisdiction} · {row.timeZone}
                </div>
                <div className="muted">created {row.createdAt.slice(0, 10)}</div>
              </div>
            </div>

            <table style={{ marginTop: 10 }}>
              <thead>
                <tr><th>Member</th><th>Role</th><th>Last seen</th></tr>
              </thead>
              <tbody>
                {row.members.map((member) => (
                  <tr key={member.userId}>
                    <td>{member.email}</td>
                    <td className="muted">{member.role}</td>
                    <td className="muted">{member.lastSeenAt?.slice(0, 10) ?? 'never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
              <div>
                <label>AI spend this month</label>
                <div className="bar" style={{ marginTop: 6 }}>
                  <span
                    style={{
                      width: `${Math.min(100, (row.spentCents / Math.max(1, row.monthlyCentsCap)) * 100)}%`,
                    }}
                  />
                </div>
                <div className="muted" style={{ marginTop: 4 }}>
                  ${(row.spentCents / 100).toFixed(2)} of ${(row.monthlyCentsCap / 100).toFixed(2)}
                </div>
              </div>
              <div className="narrow">
                <label htmlFor={`cap-${row.accountId}`}>Cap (dollars)</label>
                <input
                  id={`cap-${row.accountId}`}
                  type="number"
                  min={0}
                  defaultValue={(row.monthlyCentsCap / 100).toFixed(2)}
                  onBlur={(e) => {
                    const cents = Math.round(Number(e.target.value) * 100);
                    if (!Number.isFinite(cents) || cents === row.monthlyCentsCap) return;
                    void guard(() => api.adminUpdateAccount(row.accountId, { monthlyCentsCap: cents }).then(() => {}));
                  }}
                />
              </div>
              <div className="narrow">
                <button
                  className={row.status === 'active' ? 'danger' : ''}
                  title={
                    row.status === 'active'
                      ? 'Blocks every request from this account. Their records are untouched.'
                      : 'Restores access.'
                  }
                  onClick={() =>
                    void guard(() =>
                      api
                        .adminUpdateAccount(row.accountId, {
                          status: row.status === 'active' ? 'suspended' : 'active',
                        })
                        .then(() => {}),
                    )
                  }
                >
                  {row.status === 'active' ? 'Suspend' : 'Restore'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
